// Sets per-page <head> tags so the SPA route renders with the right SEO/OG
// metadata. Google executes JS and reads what we set here, so these tags
// drive search indexing for /p/:id and /gallery. Static social bots (Slack,
// Twitter, iMessage) don't run JS — for those we have an SSR Edge Function
// (`share-page`) that hosting redirects can point at for crawler User-Agents.
//
// Each setter mutates the live document. Call resetShareMeta() on unmount so
// the next route doesn't inherit stale per-share titles.

const DEFAULT_TITLE = "Promptwizer";
const DEFAULT_DESCRIPTION =
  "Transform your AI interactions with expertly crafted prompts. Promptwizer helps maximize AI through strategic prompt engineering.";

function setMeta(selector: string, attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export interface ShareMeta {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  noindex?: boolean;
}

export function applyShareMeta(meta: ShareMeta) {
  document.title = meta.title;
  setMeta('meta[name="description"]', "name", "description", meta.description);
  setMeta('meta[property="og:title"]', "property", "og:title", meta.title);
  setMeta('meta[property="og:description"]', "property", "og:description", meta.description);
  setMeta('meta[property="og:type"]', "property", "og:type", meta.ogType ?? "website");
  setMeta('meta[name="twitter:title"]', "name", "twitter:title", meta.title);
  setMeta('meta[name="twitter:description"]', "name", "twitter:description", meta.description);

  if (meta.canonical) {
    setMeta('meta[property="og:url"]', "property", "og:url", meta.canonical);
    setLink("canonical", meta.canonical);
  }
  if (meta.ogImage) {
    setMeta('meta[property="og:image"]', "property", "og:image", meta.ogImage);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", meta.ogImage);
  }
  if (meta.noindex) {
    setMeta('meta[name="robots"]', "name", "robots", "noindex");
  } else {
    const robots = document.head.querySelector('meta[name="robots"]');
    robots?.remove();
  }
}

export function resetShareMeta() {
  applyShareMeta({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  });
  const canonical = document.head.querySelector('link[rel="canonical"]');
  canonical?.remove();
}
